import Button from 'components/Button';
import Examples from 'components/Examples';
import { useProject } from 'providers/Project/projectHooks';
import React, { useEffect, useState } from 'react';
import { SXStyles } from 'src/types';
import { Flex, useThemeUI } from 'theme-ui';
import Mixpanel from 'util/mixpanel';
import ProjectsIcon from 'components/Icons/ProjectsIcon';
import LearnCadenceIcon from 'components/Icons/LearnCadenceIcon';
import NavInput from './NavInput';
import { ShareMenu } from './ShareMenu';
import { SaveButton } from './SaveButton';
import {
  CADENCE_DOCS_URL,
  PLAYGROUND_GITHUB_ISSUES_URL,
} from 'util/globalConstants';
import { ExportButton } from './ExportButton';
import GithubIcon from 'components/Icons/GithubIcon';
import { NavButtonLink } from './NavButtonLink';
import { IconCadence } from 'components/Icons/CadenceIcon';
import VersionInfoPopup from 'components/VersionInfoPopup';
import { ThemeToggle } from './ThemeToggle';
import { isMobile } from 'components/Editor/CadenceEditor/ControlPanel/utils';

const TopNav = () => {
  const { project, updateProject, toggleProjectsSidebar } = useProject();
  const [showExamples, setShowExamples] = useState(false);
  const [projectName, setProjectName] = useState(project.title);

  const context = useThemeUI();
  const { theme } = context;

  const styles: SXStyles = {
    root: {
      display: 'flex',
      flex: '1 1 auto',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingLeft: '1em',
      paddingRight: '1em',
      background: `${theme.colors.primary}`,
      width: '100%',
    },
    mobile: {
      background: `${theme.colors.secondaryBackground}`,
      display: 'flex',
      gridArea: 'header',
      flex: '1 1 auto',
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: '1rem',
    },
    link: {
      border: `1px solid ${theme.colors.border}`,
      background: `${theme.colors.background}`,
      fontFamily: 'body',
      color: 'text',
      textDecoration: 'none',
      display: 'flex',
      alignItems: 'center',
      margin: 0,
      fontWeight: 500,
      padding: '0.25rem ',
      borderRadius: '8px',
      fontSize: 4,
      '&:hover': {
        background: `${theme.colors.active}`,
        borderColor: `${theme.colors.accent}`,
      },
    },
    topNavSection: {
      alignItems: 'center',
      minWidth: 210,
      gap: 4,
    },
    topNavProjectName: {
      alignItems: 'center',
    },
    externalNavLink: {
      display: 'flex',
      marginLeft: '0.25rem',
      textDecoration: 'none',
    },
    externalNavLinkFlow: {
      textDecoration: 'none',
    },
    topNavSectionRight: {
      flexDirection: 'row-reverse',
    },
  };

  const onStartButtonClick = () => {
    setShowExamples(true);
    Mixpanel.track('Show examples', { meta: 'none' });
  };

  const onNameInputChange = (name: string) => {
    setProjectName(name);
  };

  const updateProjectName = (name: string) => {
    updateProject(name, project.description, project.readme);
  };

  useEffect(() => {
    setProjectName(project.title);
  }, [project?.id]);

  if (isMobile()) {
    return (
      <Flex sx={styles.mobile}>
        <div>{projectName}</div>
      </Flex>
    );
  }
  return (
    <Flex sx={styles.root}>
      <Flex sx={styles.topNavSection}>
        <Button
          onClick={toggleProjectsSidebar}
          variant="secondary"
          size="sm"
          inline={true}
        >
          <ProjectsIcon />
          Projects
        </Button>
        <Button
          onClick={() => onStartButtonClick()}
          variant="primary"
          size="sm"
          inline={true}
        >
          <LearnCadenceIcon />
          Learn Cadence
        </Button>
      </Flex>
      <Flex sx={styles.topNavProjectName}>
        <NavInput
          type="text"
          value={projectName}
          onChange={(e: any) => {
            onNameInputChange(e.target.value);
          }}
          updateValue={updateProjectName}
        />
      </Flex>
      <Flex sx={{ ...styles.topNavSection, ...styles.topNavSectionRight }}>
        {!!project && (
          <>
            <ThemeToggle />
            <VersionInfoPopup />
            <NavButtonLink
              title="Report a Bug"
              href={PLAYGROUND_GITHUB_ISSUES_URL}
            >
              <GithubIcon />
            </NavButtonLink>
            <NavButtonLink title="Cadence Docs" href={CADENCE_DOCS_URL}>
              <IconCadence size="22px" title="Cadence Language Reference" />
            </NavButtonLink>
            <ShareMenu />
            <ExportButton />
            <SaveButton />
          </>
        )}
      </Flex>
      <Examples
        visible={showExamples}
        triggerClose={() => setShowExamples(false)}
      />
    </Flex>
  );
};

export default TopNav;
